import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
// Tree-shaken icon imports for optimal bundle size
import Clock from "lucide-react/dist/esm/icons/clock";
import Flame from "lucide-react/dist/esm/icons/flame";
import Wheat from "lucide-react/dist/esm/icons/wheat";
import Beef from "lucide-react/dist/esm/icons/beef";
import Droplet from "lucide-react/dist/esm/icons/droplet";
import Bookmark from "lucide-react/dist/esm/icons/bookmark";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import Edit from "lucide-react/dist/esm/icons/edit";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import ImagePlus from "lucide-react/dist/esm/icons/image-plus";
import RotateCcw from "lucide-react/dist/esm/icons/rotate-ccw";
import Share2 from "lucide-react/dist/esm/icons/share-2";
import X from "lucide-react/dist/esm/icons/x";
import Lightbulb from "lucide-react/dist/esm/icons/lightbulb";
import Save from "lucide-react/dist/esm/icons/save";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useAuth } from "@/contexts/AuthContext";
import { useRecipe } from "@/contexts/RecipeContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { generateRecipeImage, updateRecipeImage } from "@/lib/imageUtils";
import { COMPONENT_IMAGE_CONFIG, getImageProps, getOptimizedImageUrl } from '@/lib/imageConfig';
import Image from 'next/image';
import { generateShareToken, getShareUrl, shareRecipe } from "@/lib/shareUtils";
import { componentStyles, cn } from "@/lib/design-system";
import { checkDuplicateRecipe } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import type { Recipe } from "./RecipeCard";
import type { Ingredient } from "./RecipeCard";
import { RecipeQualitySection } from "./RecipeQualitySection";
import { ShareButton } from "./ShareButton";
import { generateRecipeUrl } from "@/lib/urlUtils";

interface SavedRecipe extends Recipe {
    id: string;
}

export interface RecipeDialogProps {
    recipe: Recipe | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;

    // Dialog type and context
    dialogType: 'cookbook' | 'history' | 'community' | 'shared';

    // Action handlers
    onEdit?: (recipe: SavedRecipe) => void;
    onDelete?: (recipeId: string) => void;
    onSave?: (recipe: Recipe) => void;
    onShare?: (recipeId: string) => void;
    onImageUpdated?: (recipeId: string, newImageUrl: string) => void;
    onImageGenerated?: (newImageUrl: string) => void;

    // Custom content
    customSaveButton?: React.ReactNode;
    customHeaderActions?: React.ReactNode;

    // State
    isLoadingContent?: boolean;
    isSaving?: boolean;
    isGeneratingImage?: boolean;
}

export const RecipeDialog: React.FC<RecipeDialogProps> = ({
    recipe,
    open,
    onOpenChange,
    dialogType,
    onEdit,
    onDelete,
    onSave,
    onShare,
    onImageUpdated,
    onImageGenerated,
    customSaveButton,
    customHeaderActions,
    isLoadingContent = false,
    isSaving = false,
    isGeneratingImage = false
}) => {
    const { toast } = useToast();
    const { user } = useAuth();
    const { plan, usage, refresh: refreshSubscription } = useSubscription();
    const { markRecipeSaved, recipesGeneratingImages, markImageGenerated, markImageGenerating } = useRecipe();
    const isMobile = useIsMobile();
    const saveButtonRef = useRef<HTMLButtonElement>(null);

    const [currentRecipe, setCurrentRecipe] = useState<Recipe | null>(null);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [imageLoaded, setImageLoaded] = useState<{ [key: string]: boolean }>({});
    const [isDuplicate, setIsDuplicate] = useState(false);
    const [internalIsSaving, setInternalIsSaving] = useState(false);
    const [internalIsGeneratingImage, setInternalIsGeneratingImage] = useState(false);
    const [shareUrl, setShareUrl] = useState<string>('');
    const [isGeneratingShareUrl, setIsGeneratingShareUrl] = useState(false);

    // Use internal state if not provided via props
    const saving = isSaving || internalIsSaving;
    const generatingImage = isGeneratingImage || internalIsGeneratingImage;

    // Generate share URL when recipe opens
    useEffect(() => {
        const generateShareUrlIfNeeded = async () => {
            const recipeToShare = currentRecipe || recipe;
            if (!recipeToShare?.id || !open) {
                setShareUrl('');
                return;
            }

            const origin = typeof window !== 'undefined' ? window.location.origin : '';

            // SAVED RECIPES (not temporary): Use /recipe/{slug}-{sequence} URL
            if (recipeToShare.recipe_slug && recipeToShare.sequence_number && !recipeToShare.is_temporary) {
                const recipeUrl = generateRecipeUrl(
                    recipeToShare.id,
                    recipeToShare.title,
                    recipeToShare.sequence_number,
                    recipeToShare.version
                );
                setShareUrl(`${origin}${recipeUrl}`);
                return;
            }

            // TEMPORARY RECIPES: Use /shared/{token} URL
            // If recipe already has a share token, use it immediately
            if (recipeToShare.share_token) {
                const url = `${origin}/shared/${recipeToShare.share_token}`;
                setShareUrl(url);
                return;
            }

            // Otherwise, we'll generate the token when user clicks share
            setShareUrl('');
        };

        generateShareUrlIfNeeded();
    }, [currentRecipe, recipe, open]);

    // Load full recipe details if needed
    useEffect(() => {
        if (recipe) {
            setCurrentRecipe(recipe);

            const hasIngredients = recipe.ingredients && Array.isArray(recipe.ingredients) && recipe.ingredients.length > 0;
            const hasPreparation = Boolean(recipe.preparation && recipe.preparation.trim().length > 0);
            const hasTotalTime = Boolean(recipe.estimated_total_time && recipe.estimated_total_time.trim().length > 0);

            // Skip validation for cookbook recipes as they should have complete data
            const shouldSkipValidation = dialogType === 'cookbook' && hasIngredients && hasPreparation && hasTotalTime;

            if (!shouldSkipValidation && !(hasIngredients && hasPreparation && hasTotalTime) && (recipe as any).id) {
                setIsLoadingDetails(true);
                console.log(`📝 Recipe ${(recipe as any).id} is partial, fetching full details...`, {
                    hasIngredients,
                    hasPreparation,
                    hasTotalTime,
                    shouldSkipValidation,
                    dialogType,
                    recipeTitle: recipe.title,
                    recipePreparation: recipe.preparation,
                    recipePreparationLength: recipe.preparation?.length,
                    recipePreparationTrimmed: recipe.preparation?.trim(),
                    recipePreparationTrimmedLength: recipe.preparation?.trim()?.length,
                    recipeIngredients: recipe.ingredients,
                    recipeTotalTime: recipe.estimated_total_time,
                });

                const fetchMissingDetails = async () => {
                    try {
                        const { data, error } = await supabase
                            .from('saved_recipes')
                            .select('title, ingredients, preparation, estimated_total_time, calories_per_100g, carbs_per_100g, protein_per_100g, fat_per_100g')
                            .eq('id', (recipe as any).id)
                            .single();

                        if (error) throw error;

                        if (data) {
                            console.log(`✅ Full details fetched for ${(recipe as any).id}`);
                            setCurrentRecipe(prevRecipe => ({
                                ...prevRecipe!,
                                title: data.title || prevRecipe!.title || '',
                                ingredients: (data.ingredients as unknown) as Ingredient[] || [],
                                preparation: data.preparation || '',
                                estimated_total_time: data.estimated_total_time || '',
                                calories_per_100g: data.calories_per_100g || 0,
                                carbs_per_100g: data.carbs_per_100g || 0,
                                protein_per_100g: data.protein_per_100g || 0,
                                fat_per_100g: data.fat_per_100g || 0,
                            }));
                        }
                    } catch (err) {
                        console.error("Error fetching full recipe details:", err);
                        toast({
                            title: "שגיאה בטעינת המתכון",
                            description: "לא הצלחנו לטעון את פרטי המתכון המלאים.",
                            variant: "destructive",
                        });
                    } finally {
                        setIsLoadingDetails(false);
                    }
                };

                fetchMissingDetails();
            }
        }
    }, [recipe, toast]);

    // Check for duplicates when recipe changes
    useEffect(() => {
        if (currentRecipe && user) {
            const checkDuplicate = async () => {
                try {
                    const isDup = await checkDuplicateRecipe(currentRecipe.title, currentRecipe.ingredients, user.id);
                    setIsDuplicate(isDup);
                } catch (error) {
                    console.error("Error checking for duplicates:", error);
                }
            };
            checkDuplicate();
        }
    }, [currentRecipe, user]);

    // Handle removing recipe from cookbook
    const handleRemoveFromCookbook = async () => {
        if (!currentRecipe && !recipe) return;

        const recipeToRemove = currentRecipe || recipe!;

        // Show confirmation dialog
        const confirmed = window.confirm(
            `האם אתה בטוח שברצונך להסיר את המתכון "${recipeToRemove.title}" מספר המתכונים?`
        );

        if (!confirmed) return;

        if (onDelete) {
            onDelete((recipeToRemove as SavedRecipe).id);
        }
    };

    // Handle saving recipe
    const handleSave = async () => {
        if (!currentRecipe && !recipe) return;

        const recipeToSave = currentRecipe || recipe!;

        // For cookbook recipes, handle removal instead of saving
        if (dialogType === 'cookbook') {
            await handleRemoveFromCookbook();
            return;
        }

        if (onSave) {
            onSave(recipeToSave);
            return;
        }

        if (!user) {
            toast({
                title: "נדרשת התחברות",
                description: "התחברו כדי לשמור מתכונים",
                variant: "destructive",
            });
            return;
        }

        setInternalIsSaving(true);
        try {
            let savedRecipe: any;

            // If recipe has ID and is temporary, UPDATE it (from history)
            // Otherwise, INSERT as new recipe (from shared/community)
            if (recipeToSave.id && recipeToSave.is_temporary) {
                // UPDATE existing temporary recipe to make it permanent
                const { data, error } = await supabase
                    .from('saved_recipes')
                    .update({
                        is_temporary: false,
                        expires_at: null,
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', recipeToSave.id)
                    .eq('user_id', user.id)
                    .select()
                    .single();

                if (error) throw error;
                savedRecipe = data;
                console.log(`✅ Updated temporary recipe to permanent:`, savedRecipe);
            } else {
                // INSERT new recipe copy (from shared/community)
                const { data, error } = await supabase
                    .from('saved_recipes')
                    .insert({
                        user_id: user.id,
                        title: recipeToSave.title,
                        ingredients: recipeToSave.ingredients,
                        preparation: recipeToSave.preparation,
                        estimated_prep_time: recipeToSave.estimated_prep_time,
                        estimated_total_time: recipeToSave.estimated_total_time,
                        calories_per_100g: recipeToSave.calories_per_100g,
                        carbs_per_100g: recipeToSave.carbs_per_100g,
                        protein_per_100g: recipeToSave.protein_per_100g,
                        fat_per_100g: recipeToSave.fat_per_100g,
                        visual_dish_description_en: recipeToSave.visual_dish_description_en || null,
                        health_summary_he: recipeToSave.health_summary_he || null,
                        image: recipeToSave.image,
                        is_temporary: false
                    })
                    .select()
                    .single();

                if (error) throw error;
                savedRecipe = data;
                console.log(`✅ Created new recipe copy:`, savedRecipe);
            }

            // Trigger image generation if recipe doesn't have an image yet and has visual description
            if (savedRecipe && !savedRecipe.image && savedRecipe.visual_dish_description_en) {
                console.log(`🎨 Generating image for recipe ${savedRecipe.id}`);

                supabase.functions.invoke('generate-optimized-image', {
                    body: {
                        recipe_id: savedRecipe.id,
                        title: savedRecipe.title,
                        user_id: user.id,
                        visual_dish_description_en: savedRecipe.visual_dish_description_en
                    }
                }).catch(err => console.error('Image generation error:', err));
            }

            markRecipeSaved(recipeToSave.id);
        } catch (error) {
            console.error("Error saving recipe:", error);
            toast({
                title: "שגיאה בשמירת המתכון",
                description: "לא הצלחנו לשמור את המתכון",
                variant: "destructive",
            });
        } finally {
            setInternalIsSaving(false);
        }
    };

    // Handle generating share URL (called before opening ShareButton dropdown)
    const ensureShareToken = async () => {
        const recipeToShare = currentRecipe || recipe;
        if (!recipeToShare?.id) return '';

        // If already has share URL in state, return it
        if (shareUrl) return shareUrl;

        setIsGeneratingShareUrl(true);

        try {
            const origin = typeof window !== 'undefined' ? window.location.origin : '';

            // SAVED RECIPES (not temporary): Use /recipe/{slug}-{sequence} URL
            // These don't need a share token - they have public SEO-friendly URLs
            if (recipeToShare.recipe_slug && recipeToShare.sequence_number && !recipeToShare.is_temporary) {
                const recipeUrl = generateRecipeUrl(
                    recipeToShare.id,
                    recipeToShare.title,
                    recipeToShare.sequence_number,
                    recipeToShare.version
                );
                const url = `${origin}${recipeUrl}`;
                setShareUrl(url);
                return url;
            }

            // TEMPORARY RECIPES: Generate share token if needed
            // First check if recipe already has a share token
            const { data: existingRecipe, error: fetchError } = await supabase
                .from("saved_recipes")
                .select("share_token, title")
                .eq("id", recipeToShare.id)
                .single();

            if (fetchError) throw fetchError;

            let shareToken = existingRecipe?.share_token;

            // If no share token exists, generate one
            if (!shareToken) {
                shareToken = generateShareToken();

                // Update the recipe with the share token and make it public
                const { error: updateError } = await supabase
                    .from("saved_recipes")
                    .update({
                        share_token: shareToken,
                        is_public: true
                    })
                    .eq("id", recipeToShare.id);

                if (updateError) throw updateError;

                // Update currentRecipe state with the new token
                setCurrentRecipe(prev => prev ? { ...prev, share_token: shareToken } : null);
            }

            // Generate and save share URL
            const url = getShareUrl(shareToken);
            setShareUrl(url);
            return url;
        } catch (error) {
            console.error("Error generating share URL:", error);
            toast({
                title: "שגיאה בהכנת קישור השיתוף",
                description: "לא הצלחנו להכין את קישור השיתוף",
                variant: "destructive",
            });
            return '';
        } finally {
            setIsGeneratingShareUrl(false);
        }
    };

    // Handle image generation
    const handleGenerateImage = async () => {
        if (!currentRecipe || !user) return;

        setInternalIsGeneratingImage(true);
        try {
            const imageBlob = await generateRecipeImage(currentRecipe, { isRegeneration: true });
            if (imageBlob) {
                // Convert blob to URL for display
                const imageUrl = URL.createObjectURL(imageBlob);
                setCurrentRecipe(prev => prev ? { ...prev, image: imageUrl } : null);
                onImageGenerated?.(imageUrl);
                toast({
                    title: "תמונה נוצרה בהצלחה!",
                    description: "תמונה חדשה נוצרה למתכון",
                });
            }
        } catch (error) {
            console.error("Error generating image:", error);
            toast({
                title: "שגיאה ביצירת תמונה",
                description: "לא הצלחנו ליצור תמונה חדשה",
                variant: "destructive",
            });
        } finally {
            setInternalIsGeneratingImage(false);
        }
    };

    // Get the actual recipe title (not dialog type)
    const getRecipeTitle = () => {
        return (currentRecipe || recipe)?.title || "טוען...";
    };

    // Button layout: X, Save, Share always shown
    const showSaveButton = true; // Always show save button
    const showShareButton = true; // Always show share button
    const showEditButton = dialogType === 'cookbook' && onEdit;
    const showDeleteButton = dialogType === 'cookbook' && onDelete;
    const showImageGeneration = dialogType === 'cookbook' && user && plan?.code !== 'free';

    // Render header actions - only X button in top-left
    const renderHeaderActions = () => {
        if (customHeaderActions) {
            return customHeaderActions;
        }

        return (
            <div className="flex items-center gap-2 flex-shrink-0">
                {/* Close Button - Only X button in header */}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onOpenChange(false)}
                    className="h-10 w-10 rounded-full bg-orange-50/80 hover:bg-orange-100/80 border border-orange-200/60 hover:border-orange-300/80 transition-all shadow-sm hover:shadow-md"
                >
                    <X className="h-5 w-5 text-orange-700" />
                </Button>
            </div>
        );
    };

    // Render action buttons below title in standardized layout: [Edit] [Share] [Save]
    const renderActionButtonsBelowTitle = () => {
        return (
            <div className="flex items-center gap-3 py-3 border-b border-orange-200/30" dir="rtl">
                {/* Save/Remove Button - Always rightmost (left in RTL) */}
                {customSaveButton ? (
                    customSaveButton
                ) : (
                    <Button
                        ref={saveButtonRef}
                        onClick={handleSave}
                        disabled={saving || (dialogType !== 'cookbook' && isDuplicate)}
                        size="sm"
                        variant={dialogType === 'cookbook' ? 'outline' : 'default'}
                        className={`flex items-center gap-2 ${dialogType === 'cookbook'
                            ? 'border-red-500 text-red-600 hover:bg-red-50 hover:border-red-600 hover:text-red-700'
                            : 'bg-orange-500 hover:bg-orange-600 text-white'
                            }`}
                    >
                        {saving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : dialogType === 'cookbook' ? (
                            <Trash2 className="h-4 w-4" />
                        ) : (
                            <Bookmark className="h-4 w-4" />
                        )}
                        {dialogType === 'cookbook' ? 'הסר' : 'שמור'}
                    </Button>
                )}

                {/* Share Button - Left of save (right in RTL) */}
                {showShareButton && (
                    <ShareButton
                        url={shareUrl || ''}
                        title={displayRecipe.title}
                        description={`בוא נבשל ביחד! ${displayRecipe.title}`}
                        variant="outline"
                        size="sm"
                        showDropdown={true}
                        onBeforeOpen={async () => {
                            // Generate share token if needed before opening dropdown
                            if (!shareUrl) {
                                await ensureShareToken();
                            }
                        }}
                    />
                )}

                {/* Edit Button - Left of share (rightmost in RTL) - only for cookbook */}
                {showEditButton && (
                    <Button
                        onClick={() => onEdit!((currentRecipe || recipe) as SavedRecipe)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 border-orange-300 text-orange-700 hover:bg-orange-50 hover:border-orange-400 hover:text-orange-800"
                    >
                        <Edit className="h-4 w-4" />
                        ערוך
                    </Button>
                )}
            </div>
        );
    };

    // Render action buttons for saved recipes (image generation removed)
    const renderActionButtons = () => {
        if (dialogType !== 'cookbook') return null;

        // Image generation button removed - return null to show no action buttons
        return null;
    };

    // Loading state
    if (isLoadingContent || isLoadingDetails) {
        return (
            <>
                {isMobile ? (
                    <Drawer open={open} onOpenChange={onOpenChange}>
                        <DrawerContent className="max-h-[90vh] flex flex-col bg-gradient-to-b from-orange-50/40 via-amber-50/30 to-orange-100/40 border-t border-orange-200/40" dir="rtl">
                            <DrawerHeader className="px-4 shrink-0 pb-4">
                                {/* Header with inline title and buttons in RTL layout */}
                                <div className="flex items-start gap-4" dir="rtl">
                                    {/* Recipe Title - Right side in RTL, can wrap to 2 lines */}
                                    <DrawerTitle className="text-2xl font-bold text-orange-950 leading-tight flex-1 min-w-0 pl-2">
                                        {getRecipeTitle()}
                                    </DrawerTitle>
                                    {renderHeaderActions()}
                                </div>
                            </DrawerHeader>
                            <div className="px-4 pb-4">
                                <div className="space-y-4">
                                    <Skeleton className="h-48 w-full" />
                                    <div className="space-y-2">
                                        <Skeleton className="h-6 w-3/4" />
                                        <Skeleton className="h-4 w-full" />
                                        <Skeleton className="h-4 w-5/6" />
                                    </div>
                                </div>
                            </div>
                        </DrawerContent>
                    </Drawer>
                ) : (
                    <Dialog open={open} onOpenChange={onOpenChange}>
                        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col bg-gradient-to-b from-orange-50/40 via-amber-50/30 to-orange-100/40 border border-orange-200/40 shadow-2xl p-0" dir="rtl" hideCloseButton>
                            <DialogDescription className="sr-only">
                                מתכון מפורט עם כל הפרטים וההוראות
                            </DialogDescription>
                            <DialogHeader className="pb-4 p-6 shrink-0">
                                {/* Header with inline title and buttons in RTL layout */}
                                <div className="flex items-start gap-4" dir="rtl">
                                    {/* Recipe Title - Right side in RTL, can wrap to 2 lines */}
                                    <DialogTitle className="text-2xl font-bold text-orange-950 leading-tight flex-1 min-w-0 pl-2 text-right" dir="rtl">
                                        {getRecipeTitle()}
                                    </DialogTitle>
                                    {renderHeaderActions()}
                                </div>
                            </DialogHeader>
                            <div className="overflow-y-auto flex-1 p-6 pt-4 pb-8">
                                <div className="space-y-6">
                                    <Skeleton className="h-64 w-full" />
                                    <div className="space-y-4">
                                        <Skeleton className="h-6 w-3/4" />
                                        <Skeleton className="h-4 w-full" />
                                        <Skeleton className="h-4 w-5/6" />
                                    </div>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                )}
            </>
        );
    }

    // No recipe state
    if (!currentRecipe && !recipe) {
        return null;
    }

    const displayRecipe = currentRecipe || recipe!;

    // Main dialog content - using exact same styling as RecipeDetailDialog
    const dialogContent = (
        <div className="space-y-6">
            {/* Recipe Image - Always reserve space to prevent dialog jumping */}
            <div className="relative h-64">
                {/* Always show placeholder first */}
                <div className="w-full h-64 bg-gradient-to-br from-orange-100/50 to-amber-100/50 rounded-2xl shadow-sm flex items-center justify-center">
                    <div className="text-center text-orange-600/60">
                        <div className="text-4xl mb-2">🍽️</div>
                        <div className="text-sm font-medium">טוען תמונה...</div>
                    </div>
                </div>

                {/* Overlay image when loaded */}
                {displayRecipe.image && (
                    <Image
                        src={getOptimizedImageUrl(displayRecipe.image, COMPONENT_IMAGE_CONFIG.RECIPE_DETAIL.size, COMPONENT_IMAGE_CONFIG.RECIPE_DETAIL.quality)}
                        alt={`תמונה של מתכון: ${displayRecipe.title}`}
                        fill
                        className={`absolute inset-0 object-cover rounded-2xl shadow-lg transition-opacity duration-300 ${imageLoaded[displayRecipe.id || ''] ? 'opacity-100' : 'opacity-0'
                            }`}
                        {...getImageProps(COMPONENT_IMAGE_CONFIG.RECIPE_DETAIL)}
                        onLoad={() => {
                            if (displayRecipe.id) {
                                setImageLoaded(prev => ({ ...prev, [displayRecipe.id!]: true }));
                            }
                        }}
                        onError={() => {
                            if (displayRecipe.id) {
                                setImageLoaded(prev => ({ ...prev, [displayRecipe.id!]: false }));
                            }
                        }}
                    />
                )}
            </div>

            {/* Action Buttons Below Title (not sticky) */}
            {renderActionButtonsBelowTitle()}

            {/* Dish Origin Context - For all recipes that have this property */}
            {displayRecipe?.dish_origin_context_he && displayRecipe.dish_origin_context_he.trim() && (
                <div className="bg-gradient-to-br from-amber-50/60 to-yellow-50/50 rounded-2xl p-5 border border-amber-200/40 shadow-sm">
                    <div className="flex items-start gap-3" dir="rtl">
                        <div className="flex-shrink-0 text-2xl">🌍</div>
                        <div className="flex-1">
                            <h3 className="text-base font-bold text-amber-800 mb-2">רקע תרבותי</h3>
                            <p className="text-gray-800 text-base leading-relaxed" itemProp="recipeOrigin">
                                {displayRecipe.dish_origin_context_he}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Nutritional Info - Cozy Warm Design */}
            <div className="bg-gradient-to-br from-orange-50/60 to-amber-50/50 rounded-2xl p-6 border border-orange-200/30 shadow-sm">
                <h3 className="text-center text-lg font-bold text-orange-800 mb-5 tracking-wide">ערכים תזונתיים (לכל 100 גרם)</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" dir="rtl">
                    {/* Calories */}
                    <div className="flex flex-col items-center gap-2.5 text-center p-4 rounded-2xl bg-orange-50/50 backdrop-blur-sm border border-orange-200/30 hover:bg-orange-50/70 transition-colors shadow-sm">
                        <div className="p-2 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl shadow-sm">
                            <Flame className="h-4 w-4 text-white" />
                        </div>
                        <div className="text-3xl font-bold text-orange-900 leading-none">
                            {displayRecipe?.calories_per_100g != null ? Math.round(Number(displayRecipe.calories_per_100g)) : '—'}
                        </div>
                        <div className="text-xs font-semibold text-orange-700">קלוריות</div>
                    </div>

                    {/* Carbs */}
                    <div className="flex flex-col items-center gap-2.5 text-center p-4 rounded-2xl bg-amber-50/50 backdrop-blur-sm border border-amber-200/30 hover:bg-amber-50/70 transition-colors shadow-sm">
                        <div className="p-2 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl shadow-sm">
                            <Wheat className="h-4 w-4 text-white" />
                        </div>
                        <div className="text-3xl font-bold text-amber-900 leading-none">
                            {displayRecipe?.carbs_per_100g != null ? Math.round(Number(displayRecipe.carbs_per_100g)) : '—'}
                            <span className="text-lg">g</span>
                        </div>
                        <div className="text-xs font-semibold text-amber-700">פחמימות</div>
                    </div>

                    {/* Protein */}
                    <div className="flex flex-col items-center gap-2.5 text-center p-4 rounded-2xl bg-red-50/50 backdrop-blur-sm border border-red-200/30 hover:bg-red-50/70 transition-colors shadow-sm">
                        <div className="p-2 bg-gradient-to-br from-red-400 to-red-500 rounded-xl shadow-sm">
                            <Beef className="h-4 w-4 text-white" />
                        </div>
                        <div className="text-3xl font-bold text-red-900 leading-none">
                            {displayRecipe?.protein_per_100g != null ? Math.round(Number(displayRecipe.protein_per_100g)) : '—'}
                            <span className="text-lg">g</span>
                        </div>
                        <div className="text-xs font-semibold text-red-700">חלבון</div>
                    </div>

                    {/* Fat */}
                    <div className="flex flex-col items-center gap-2.5 text-center p-4 rounded-2xl bg-blue-50/50 backdrop-blur-sm border border-blue-200/30 hover:bg-blue-50/70 transition-colors shadow-sm">
                        <div className="p-2 bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl shadow-sm">
                            <Droplet className="h-4 w-4 text-white" />
                        </div>
                        <div className="text-3xl font-bold text-blue-900 leading-none">
                            {displayRecipe?.fat_per_100g != null ? Math.round(Number(displayRecipe.fat_per_100g)) : '—'}
                            <span className="text-lg">g</span>
                        </div>
                        <div className="text-xs font-semibold text-blue-700">שומן</div>
                    </div>
                </div>
            </div>

            {/* Quality Section - Only for history recipes with score <= 85 */}
            {dialogType === 'history' && (
                <RecipeQualitySection
                    qualityScore={displayRecipe.recipe_quality_score}
                    qualityExplanation={displayRecipe.quality_explanation_he}
                />
            )}


            {/* Time Info - Cozy Warm Layout */}
            <div className="bg-gradient-to-br from-orange-50/60 to-amber-50/50 rounded-2xl p-5 border border-orange-200/30 shadow-sm">
                <h3 className="text-center text-lg font-bold text-orange-800 mb-4 tracking-wide">זמני הכנה</h3>
                <div className="flex items-center justify-center gap-8" dir="rtl">
                    {displayRecipe?.estimated_prep_time && (
                        <div className="flex items-center gap-3">
                            <Clock className="h-4 w-4 text-orange-400/60" />
                            <div className="text-right">
                                <div className="text-sm font-bold text-orange-700">התעסקות</div>
                                <div className="text-xl font-bold text-orange-900">{displayRecipe.estimated_prep_time}</div>
                            </div>
                        </div>
                    )}
                    {displayRecipe?.estimated_prep_time && displayRecipe?.estimated_total_time && (
                        <div className="h-10 w-px bg-orange-300/40"></div>
                    )}
                    {displayRecipe?.estimated_total_time && (
                        <div className="flex items-center gap-3">
                            <Clock className="h-4 w-4 text-orange-400/60" />
                            <div className="text-right">
                                <div className="text-sm font-bold text-orange-700">כולל</div>
                                <div className="text-xl font-bold text-orange-900">{displayRecipe.estimated_total_time}</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>


            {/* Ingredients - Cozy Warm Section */}
            <div className="bg-gradient-to-br from-orange-50/60 to-amber-50/50 rounded-2xl p-6 border border-orange-200/30 shadow-sm">
                <h3 className="text-center text-lg font-bold text-orange-800 mb-5 tracking-wide">מצרכים</h3>
                {isLoadingDetails ? (
                    <div className="space-y-2">
                        {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}
                    </div>
                ) : (
                    <div className="space-y-2">
                        {(displayRecipe?.ingredients as { name: string; amount: string }[] || []).map((ingredient, index) => (
                            <div key={index} className="flex items-center gap-3 p-4 rounded-xl bg-orange-50/40 backdrop-blur-sm border border-orange-100/30 hover:bg-orange-50/60 transition-colors shadow-sm" dir="rtl">
                                <p className="text-gray-800 text-lg leading-relaxed">
                                    <span className="font-bold text-gray-900">{ingredient.name}</span>
                                    <span className="text-orange-400/60 mx-3">·</span>
                                    <span className="text-gray-700">{ingredient.amount}</span>
                                </p>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Preparation - Cozy Warm Section */}
            <div className="bg-gradient-to-br from-orange-50/60 to-amber-50/50 rounded-2xl p-6 border border-orange-200/30 shadow-sm">
                <h3 className="text-center text-lg font-bold text-orange-800 mb-5 tracking-wide">אופן ההכנה</h3>
                {isLoadingDetails ? (
                    <div className="space-y-3">
                        {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-6 w-full rounded-lg" />)}
                    </div>
                ) : (
                    <div className="space-y-4 text-right" dir="rtl">
                        {(() => {
                            const text = displayRecipe?.preparation || '';
                            const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
                            if (lines.length === 0) {
                                return <p className="text-muted-foreground text-center py-8">אין הוראות הכנה</p>;
                            }

                            let currentSection = '';
                            let stepCounter = 0;

                            return lines.map((line, idx) => {
                                const stepMatch = line.match(/^(\d+)[\.)]\s*(.*)$/);
                                if (stepMatch) {
                                    const num = stepMatch[1];
                                    const content = stepMatch[2] || '';
                                    stepCounter++;
                                    return (
                                        <div key={idx} className="flex items-start gap-4 bg-orange-50/40 backdrop-blur-sm border border-orange-100/30 rounded-xl p-4 hover:bg-orange-50/60 transition-colors shadow-sm">
                                            <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-orange-400 to-orange-500 text-white flex items-center justify-center text-sm font-bold shadow-sm">
                                                {num}
                                            </div>
                                            <div className="flex-1 pt-0.5">
                                                <p className="text-gray-800 leading-relaxed text-lg">{content}</p>
                                            </div>
                                        </div>
                                    );
                                }

                                if (line.startsWith('-')) {
                                    const content = line.replace(/^-+\s*/, '');
                                    return (
                                        <div key={idx} className="flex items-start gap-3.5 bg-gradient-to-l from-amber-50/60 to-yellow-50/40 border-r-4 border-amber-400/70 rounded-xl p-4 shadow-sm backdrop-blur-sm">
                                            <Lightbulb className="flex-shrink-0 w-5 h-5 text-amber-700 mt-0.5" />
                                            <p className="text-gray-900 text-lg leading-relaxed font-medium">{content}</p>
                                        </div>
                                    );
                                }

                                // Section header
                                currentSection = line;
                                return (
                                    <div key={idx} className="pt-4 pb-1">
                                        <h4 className="text-base font-bold text-orange-800 text-center">
                                            {line}
                                        </h4>
                                    </div>
                                );
                            });
                        })()}
                    </div>
                )}
            </div>


            {/* Action Buttons */}
            {renderActionButtons()}
        </div>
    );

    // Render based on device type
    if (isMobile) {
        return (
            <Drawer open={open} onOpenChange={onOpenChange}>
                <DrawerContent className="max-h-[90vh] flex flex-col bg-gradient-to-b from-orange-50/40 via-amber-50/30 to-orange-100/40 border-t border-orange-200/40" dir="rtl">
                    {/* Sticky Header */}
                    <div className="sticky top-0 z-10 bg-gradient-to-b from-orange-50/40 via-amber-50/30 to-orange-100/40 border-b border-orange-200/40 px-4 py-4 shrink-0">
                        <div className="flex items-start gap-4" dir="rtl">
                            <DrawerTitle className="text-2xl font-bold text-orange-950 leading-tight flex-1 min-w-0 pl-2">
                                {getRecipeTitle()}
                            </DrawerTitle>
                            {renderHeaderActions()}
                        </div>
                    </div>

                    {/* Scrollable Content */}
                    <div className="drawer-scrollable-content overflow-y-auto px-4 pb-4 flex-1">
                        {dialogContent}
                    </div>
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col bg-gradient-to-b from-orange-50/40 via-amber-50/30 to-orange-100/40 border border-orange-200/40 shadow-2xl p-0" dir="rtl" hideCloseButton>
                <DialogDescription className="sr-only">
                    {recipe ? `מתכון: ${recipe.title}` : "טוען פרטי המתכון..."}
                </DialogDescription>
                {/* Sticky Header */}
                <div className="sticky top-0 z-10 bg-gradient-to-b from-orange-50/40 via-amber-50/30 to-orange-100/40 border-b border-orange-200/40 p-6 pb-4 shrink-0">
                    <div className="flex items-start gap-4" dir="rtl">
                        <DialogTitle className="text-2xl font-bold text-orange-950 leading-tight flex-1 min-w-0 pl-2 text-right" dir="rtl">
                            {getRecipeTitle()}
                        </DialogTitle>
                        {renderHeaderActions()}
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="overflow-y-auto flex-1 p-6 pt-4 pb-8">
                    {dialogContent}
                </div>
            </DialogContent>
        </Dialog>
    );
};
